import type { EditorRemixLifecycleEndReason } from '@atlassian/malleable-ui-components/types';

/** Object Sidebar lifecycle projection and parity classification. */
export type ObjectSidebarRemixEntryPath =
	| 'popup-open'
	| 'popup-option'
	| 'rovo-sidebar'
	| 'unknown';

export type ObjectSidebarRemixPhase =
	| 'inactive'
	| 'thinking'
	| 'ready'
	| 'previewing'
	| 'handed-off'
	| 'inserted'
	| 'insertion-failed'
	| 'error'
	| 'aborted';

export type ObjectSidebarRemixComparisonPoint =
	| 'flow-started'
	| 'result-ready'
	| 'preview-opened'
	| 'handed-off'
	| 'inserted'
	| 'insertion-failed'
	| 'error'
	| 'aborted'
	| 'released';

export type ObjectSidebarRemixDivergenceReason =
	| 'legacy-not-tracking'
	| 'terminal-outcome-mismatch'
	| 'released-unresolved';

export type ObjectSidebarRemixDivergence = Readonly<{
	legacyPhase: ObjectSidebarRemixPhase;
	machinePhase: ObjectSidebarRemixPhase;
	comparisonPoint: ObjectSidebarRemixComparisonPoint;
	divergenceReason: ObjectSidebarRemixDivergenceReason;
}>;

const terminal = (phase: ObjectSidebarRemixPhase): boolean =>
	phase === 'handed-off' ||
	phase === 'inserted' ||
	phase === 'insertion-failed' ||
	phase === 'error' ||
	phase === 'aborted';

const pointByLegacyPhase = {
	inactive: 'released',
	thinking: 'flow-started',
	ready: 'result-ready',
	previewing: 'preview-opened',
	'handed-off': 'handed-off',
	inserted: 'inserted',
	'insertion-failed': 'insertion-failed',
	error: 'error',
	aborted: 'aborted',
} as const satisfies Record<ObjectSidebarRemixPhase, ObjectSidebarRemixComparisonPoint>;

export const objectSidebarComparisonPointForLegacyPhase = (
	phase: ObjectSidebarRemixPhase,
): ObjectSidebarRemixComparisonPoint => pointByLegacyPhase[phase];

const phaseByEndReason = {
	'handed-off': 'handed-off',
	error: 'error',
	aborted: 'aborted',
	// Same mapping as PageRemixModal.onClose.
	cancelled: 'aborted',
	// Outcome unknown by contract, so it is never synthesised.
	abandoned: undefined,
} as const satisfies Record<EditorRemixLifecycleEndReason, ObjectSidebarRemixPhase | undefined>;

/** The phase a side reaches when `end(reason)` settles it. Terminal and inactive phases are kept. */
export const settleObjectSidebarPhaseAtEnd = (
	phase: ObjectSidebarRemixPhase,
	endReason: EditorRemixLifecycleEndReason | undefined,
): ObjectSidebarRemixPhase =>
	endReason === undefined || phase === 'inactive' || terminal(phase)
		? phase
		: (phaseByEndReason[endReason] ?? phase);

export const compareObjectSidebarRemixPhases = (
	legacyPhase: ObjectSidebarRemixPhase,
	machinePhase: ObjectSidebarRemixPhase,
): 'match' | 'pending' | ObjectSidebarRemixDivergence => {
	if (legacyPhase === machinePhase) {
		return 'match';
	}
	if (legacyPhase === 'inactive' && machinePhase !== 'inactive') {
		return {
			legacyPhase,
			machinePhase,
			comparisonPoint: pointByLegacyPhase[legacyPhase],
			divergenceReason: 'legacy-not-tracking',
		};
	}
	if (terminal(legacyPhase) && terminal(machinePhase)) {
		return {
			legacyPhase,
			machinePhase,
			comparisonPoint: pointByLegacyPhase[legacyPhase],
			divergenceReason: 'terminal-outcome-mismatch',
		};
	}
	// A nonterminal mismatch is timing mechanics, even after the settle window. The window
	// determines when to report it; it never promotes ordinary phase skew to a critical event.
	return 'pending';
};
